import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Star, Plus, Trophy, Sparkle, Heart } from "@phosphor-icons/react";

const VALUES = [
  { v: "team_player", label: "Team player", color: "bg-blue-50 text-blue-800 border-blue-200" },
  { v: "innovation", label: "Innovation", color: "bg-purple-50 text-purple-800 border-purple-200" },
  { v: "customer_first", label: "Customer first", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { v: "ownership", label: "Ownership", color: "bg-orange-50 text-orange-800 border-orange-200" },
  { v: "excellence", label: "Excellence", color: "bg-adb-navy/10 text-adb-navy border-adb-navy/30" },
  { v: "kindness", label: "Kindness", color: "bg-pink-50 text-pink-800 border-pink-200" },
];

const POINT_OPTIONS = [10, 25, 50, 100];

export default function Kudos() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_employee_id: "", message: "", value_tag: "team_player", points: 10 });
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  const load = () => {
    api.get("/kudos", { params }).then(({ data }) => setRows(data)).catch(() => {});
    api.get("/kudos/leaderboard", { params }).then(({ data }) => setLeaderboard(data)).catch(() => {});
    api.get("/employees", { params }).then(({ data }) => setEmployees(data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const give = async () => {
    try {
      await api.post("/kudos", { ...form, points: parseInt(form.points) });
      toast.success("Kudos sent 🎉");
      setOpen(false);
      setForm({ to_employee_id: "", message: "", value_tag: "team_player", points: 10 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const valueOf = (t) => VALUES.find((v) => v.v === t) || VALUES[0];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Engagement</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Kudos & Recognition</h1>
          <p className="text-sm text-zinc-500 mt-2">Celebrate peers who live our values. Give kudos, accumulate points, climb the wall.</p>
        </div>
        {user?.employee_id && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="kudos-give-btn">
                <Sparkle size={16} weight="fill" className="mr-1.5" /> Give kudos
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Recognize a peer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Who deserves this? *</Label>
                  <Select value={form.to_employee_id} onValueChange={(v) => setForm({ ...form, to_employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="kudos-to-select"><SelectValue placeholder="Select colleague" /></SelectTrigger>
                    <SelectContent>
                      {employees.filter((e) => e.id !== user.employee_id).map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Message *</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell them what they did that was awesome…" rows={3} className="rounded-sm" data-testid="kudos-message-input" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Value they lived</Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {VALUES.map((v) => (
                      <button key={v.v} type="button" onClick={() => setForm({ ...form, value_tag: v.v })} data-testid={`kudos-tag-${v.v}`}
                        className={`text-xs px-2 py-2 border rounded-sm transition-all ${form.value_tag === v.v ? `${v.color} border-current font-semibold` : "border-zinc-200 hover:border-zinc-400 bg-white"}`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Points</Label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {POINT_OPTIONS.map((p) => (
                      <button key={p} type="button" onClick={() => setForm({ ...form, points: p })} data-testid={`kudos-pts-${p}`}
                        className={`text-sm px-2 py-2 border rounded-sm font-mono-plex font-semibold ${form.points === p ? "bg-adb-green/20 border-adb-green text-adb-navy" : "border-zinc-200 hover:border-zinc-400 bg-white"}`}>
                        +{p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={give} disabled={!form.to_employee_id || !form.message.trim()} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="kudos-send-btn">
                  <Sparkle size={14} weight="fill" className="mr-1" /> Send kudos
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3" data-testid="kudos-wall">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">The wall</div>
          {rows.length === 0 && (
            <div className="border border-zinc-200 bg-white rounded-sm p-12 text-center">
              <div className="text-4xl">🌟</div>
              <div className="font-display text-lg font-bold mt-2">Be the first to give kudos</div>
              <div className="text-xs text-zinc-500">Recognition is contagious.</div>
            </div>
          )}
          {rows.map((r) => {
            const v = valueOf(r.value_tag);
            return (
              <div key={r.id} className="border border-zinc-200 bg-white rounded-sm p-5 hover:shadow-sm hover:border-adb-navy/40 transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-adb-navy text-white grid place-items-center font-display font-bold text-sm shrink-0">
                    {(r.from_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-semibold text-adb-navy">{r.from_name}</span>
                      <span className="text-zinc-500"> gave kudos to </span>
                      <span className="font-semibold text-adb-navy">{r.to_name}</span>
                    </div>
                    <div className="text-sm text-zinc-800 mt-1.5 leading-relaxed">"{r.message}"</div>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Badge variant="outline" className={`rounded-sm ${v.color}`}>#{v.label}</Badge>
                      <Badge variant="outline" className="rounded-sm bg-adb-green/10 border-adb-green text-adb-navy font-mono-plex font-semibold">+{r.points} pts</Badge>
                      <span className="text-[10px] text-zinc-400 font-mono-plex ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5"><Trophy size={12} className="text-adb-green" weight="fill" /> Leaderboard</div>
          <div className="border border-zinc-200 bg-white rounded-sm" data-testid="kudos-leaderboard">
            {leaderboard.length === 0 && <div className="p-8 text-center text-xs text-zinc-400">No rankings yet.</div>}
            {leaderboard.map((r, i) => (
              <div key={r.employee_id} className={`flex items-center gap-2.5 px-3 py-2.5 border-b border-zinc-100 last:border-0 ${i === 0 ? "bg-gradient-to-r from-adb-green/10 to-transparent" : ""}`}>
                <div className={`w-7 h-7 grid place-items-center rounded-sm font-display font-bold text-xs ${i === 0 ? "bg-adb-green text-adb-navy" : i === 1 ? "bg-zinc-200 text-zinc-700" : i === 2 ? "bg-orange-100 text-orange-800" : "bg-zinc-50 text-zinc-500"}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{r.employee_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-plex">{r.kudos_count} kudos</div>
                </div>
                <div className="text-sm font-display font-bold text-adb-navy font-mono-plex flex items-center gap-1">
                  <Star size={12} weight="fill" className="text-adb-green" /> {r.points}
                </div>
              </div>
            ))}
          </div>

          <div className="border border-adb-navy/20 bg-adb-navy/5 rounded-sm p-4">
            <Heart size={14} weight="fill" className="text-adb-green" />
            <div className="font-display text-sm font-bold mt-1.5">Recognition matters.</div>
            <div className="text-xs text-zinc-600 mt-1">Teams with regular kudos rituals see 31% higher retention. Make it a habit.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
